import { useState, useEffect, useRef, useCallback } from 'react';
import { createApiUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import {
  SYNC_MESSAGE,
  REPORT_SIDEBAR_WINDOW_NAME,
  DEFAULT_REPORT_EDITOR_SETTINGS,
  DEFAULT_LAYOUT_SETTINGS,
  normalizeReportEditorSettings,
  normalizeLayoutSettings,
  getPopupFeatures,
  readWindowGeometry,
  postReportSidebarMessage,
  subscribeReportSidebarMessages,
} from '../../../utils/reportSidebarSync';

let settingsSaveTimer = null;

async function fetchReportEditorSettings() {
  const response = await fetch(createApiUrl('profile/report-editor-settings'), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) return { ...DEFAULT_REPORT_EDITOR_SETTINGS };
  const data = await response.json();
  return normalizeReportEditorSettings(data.report_editor_settings);
}

async function saveReportEditorSettings(settings) {
  await fetch(createApiUrl('profile/report-editor-settings'), {
    method: 'PUT',
    headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ report_editor_settings: settings }),
  });
}

function debouncedSaveSettings(settings) {
  if (settingsSaveTimer) clearTimeout(settingsSaveTimer);
  settingsSaveTimer = setTimeout(() => {
    saveReportEditorSettings(settings).catch((err) => {
      console.error('[ReportSidebar] Failed to save settings:', err);
    });
  }, 500);
}

export default function useReportSidebarPopup({
  enabled = true,
  reportContext,
  onDock,
  onPopupBlocked,
}) {
  const [sidebarDetached, setSidebarDetached] = useState(false);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [layoutSettings, setLayoutSettings] = useState({ ...DEFAULT_LAYOUT_SETTINGS });
  const settingsRef = useRef({ ...DEFAULT_REPORT_EDITOR_SETTINGS });
  const popupRef = useRef(null);
  const reportContextRef = useRef(reportContext);

  reportContextRef.current = reportContext;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    fetchReportEditorSettings().then((settings) => {
      if (cancelled) return;
      settingsRef.current = settings;
      setSidebarDetached(Boolean(settings.sidebarDetached));
      setLayoutSettings(normalizeLayoutSettings(settings.layout));
      setSettingsLoaded(true);
    });

    return () => { cancelled = true; };
  }, [enabled]);

  const persistSettings = useCallback((partial) => {
    settingsRef.current = { ...settingsRef.current, ...partial };
    debouncedSaveSettings(settingsRef.current);
  }, []);

  const updateLayoutSettings = useCallback((partial) => {
    setLayoutSettings((prev) => {
      const next = normalizeLayoutSettings({ ...prev, ...partial });
      persistSettings({ layout: next });
      return next;
    });
  }, [persistSettings]);

  const openPopupWindow = useCallback(() => {
    const features = getPopupFeatures(settingsRef.current.popupWindow);
    const existing = window.open('', REPORT_SIDEBAR_WINDOW_NAME);
    if (existing && !existing.closed) {
      existing.focus();
      if (existing.location.pathname !== '/report-sidebar-popup') {
        existing.location.href = '/report-sidebar-popup';
      }
      popupRef.current = existing;
      setPopupOpen(true);
      return existing;
    }

    const popup = window.open('/report-sidebar-popup', REPORT_SIDEBAR_WINDOW_NAME, features);
    if (!popup) {
      onPopupBlocked?.();
      return null;
    }
    popupRef.current = popup;
    setPopupOpen(true);
    return popup;
  }, [onPopupBlocked]);

  const broadcastContext = useCallback((context) => {
    postReportSidebarMessage(SYNC_MESSAGE.CONTEXT_UPDATE, context || reportContextRef.current || {});
  }, []);

  const broadcastClear = useCallback((reason = 'navigate') => {
    postReportSidebarMessage(SYNC_MESSAGE.CONTEXT_CLEAR, { reason });
  }, []);

  const dockSidebar = useCallback(({ keepPreference = true } = {}) => {
    if (popupRef.current && !popupRef.current.closed) {
      popupRef.current.close();
    }
    popupRef.current = null;
    setPopupOpen(false);
    if (!keepPreference) {
      setSidebarDetached(false);
      persistSettings({ sidebarDetached: false });
    }
    onDock?.();
  }, [onDock, persistSettings]);

  const detachSidebar = useCallback(() => {
    setSidebarDetached(true);
    persistSettings({ sidebarDetached: true });
    const popup = openPopupWindow();
    if (popup) {
      setTimeout(() => broadcastContext(reportContextRef.current), 300);
    }
  }, [openPopupWindow, broadcastContext, persistSettings]);

  const toggleDetach = useCallback(() => {
    if (popupOpen) {
      // Explizites Andocken: Präferenz auf "angedockt" persistieren, damit
      // der Zustand nach Reload/erneutem Öffnen erhalten bleibt.
      dockSidebar({ keepPreference: false });
    } else {
      detachSidebar();
    }
  }, [popupOpen, dockSidebar, detachSidebar]);

  useEffect(() => {
    if (!enabled || !settingsLoaded) return undefined;

    if (sidebarDetached) {
      const popup = openPopupWindow();
      if (popup) {
        setTimeout(() => broadcastContext(reportContextRef.current), 400);
      }
    }

    return undefined;
  }, [enabled, settingsLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!enabled || !sidebarDetached || !reportContext?.patientId) return;
    broadcastContext(reportContext);
  }, [enabled, sidebarDetached, reportContext, broadcastContext]);

  useEffect(() => {
    if (!enabled) return undefined;

    return subscribeReportSidebarMessages((message) => {
      if (message.type === SYNC_MESSAGE.POPUP_CLOSED) {
        popupRef.current = null;
        setPopupOpen(false);
        // Popup wurde geschlossen -> Inhalt ist wieder angedockt.
        // Präferenz auf "angedockt" setzen, damit Anzeige und persistierter
        // Zustand übereinstimmen (Popup-Geometrie bleibt für spätere Nutzung erhalten).
        setSidebarDetached(false);
        const partial = { sidebarDetached: false };
        if (message.payload?.geometry) {
          partial.popupWindow = message.payload.geometry;
        }
        persistSettings(partial);
        onDock?.();
        return;
      }

      if (message.type === SYNC_MESSAGE.DOCK) {
        dockSidebar({ keepPreference: message.payload?.keepPreference !== false });
        return;
      }

      if (message.type === SYNC_MESSAGE.POPUP_GEOMETRY && message.payload) {
        persistSettings({ popupWindow: message.payload });
      }
    });
  }, [enabled, dockSidebar, onDock, persistSettings]);

  useEffect(() => {
    if (!enabled) return undefined;

    const interval = setInterval(() => {
      if (popupRef.current && popupRef.current.closed) {
        popupRef.current = null;
        setPopupOpen(false);
        onDock?.();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, onDock]);

  useEffect(() => {
    if (!enabled) return undefined;
    return () => {
      broadcastClear('unmount');
    };
  }, [enabled, broadcastClear]);

  return {
    sidebarDetached,
    popupOpen,
    settingsLoaded,
    layoutSettings,
    updateLayoutSettings,
    toggleDetach,
    detachSidebar,
    dockSidebar,
    broadcastContext,
    broadcastClear,
    persistSettings,
    settingsRef,
    popupRef,
  };
}

export function useReportSidebarPopupWindow({ onDockRequest } = {}) {
  const geometryTimerRef = useRef(null);

  useEffect(() => {
    document.title = 'Report Sidebar';

    const reportGeometry = () => {
      const geometry = readWindowGeometry(window);
      if (geometry) {
        postReportSidebarMessage(SYNC_MESSAGE.POPUP_GEOMETRY, geometry);
      }
    };

    const onResize = () => {
      if (geometryTimerRef.current) clearTimeout(geometryTimerRef.current);
      geometryTimerRef.current = setTimeout(reportGeometry, 500);
    };

    window.addEventListener('resize', onResize);
    window.addEventListener('move', onResize);

    const handleBeforeUnload = () => {
      reportGeometry();
      postReportSidebarMessage(SYNC_MESSAGE.POPUP_CLOSED, {
        geometry: readWindowGeometry(window),
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('move', onResize);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (geometryTimerRef.current) clearTimeout(geometryTimerRef.current);
    };
  }, []);

  const requestDock = useCallback(() => {
    // Explizites Andocken aus dem Popup: Präferenz auf "angedockt" persistieren.
    postReportSidebarMessage(SYNC_MESSAGE.DOCK, { keepPreference: false });
    onDockRequest?.();
    window.close();
  }, [onDockRequest]);

  return { requestDock };
}

export function useReportSidebarContextListener(onContextUpdate, onContextClear) {
  const [context, setContext] = useState(null);

  useEffect(() => {
    return subscribeReportSidebarMessages((message) => {
      if (message.type === SYNC_MESSAGE.CONTEXT_UPDATE) {
        setContext(message.payload || null);
        onContextUpdate?.(message.payload || null);
      }
      if (message.type === SYNC_MESSAGE.CONTEXT_CLEAR) {
        setContext(null);
        onContextClear?.(message.payload);
      }
    });
  }, [onContextUpdate, onContextClear]);

  return context;
}
